/**
 * 輪詢 composable —— 全 repo 第一個計時器抽象（代管中台各頁的即時感靠它，沒有 WS）。
 *
 * 四條硬規則（踩過就知道為什麼）：
 * 1. **`onUnmounted` 必清**——離開頁面還在跑＝背景打 API、SPA 換頁後噴 401 導登入。
 * 2. **`document.hidden` 暫停**——分頁切走不燒後端；切回來**立刻補跑一次**再排下一輪。
 * 3. **同時只跑一個請求**——用自排程 `setTimeout`（非 `setInterval`）＋in-flight 旗標，
 *    慢回應不會疊成請求風暴。
 * 4. **錯誤不中斷後續輪次**——LS 短暫 5xx／網路抖動照樣要自己復原，不能一次失敗就停手。
 *
 * 用法：`const poll = usePolling(load, 15_000)`——setup 期間呼叫即可（會自己 mount／unmount）；
 * 寫入動作完成後手動 `poll.refresh()` 拿最新狀態，不必等下一輪。
 */
import { onUnmounted } from 'vue'

export interface PollingHandle {
  /** 立刻跑一次；已有請求在飛時**排一次補跑**（不疊加、也不漏掉這次要求）。 */
  refresh: () => Promise<void>
  /** 停止排程（不影響已在飛的請求）。 */
  stop: () => void
  /** 重新開始排程（`stop()` 之後用）。 */
  start: () => void
}

export interface PollingOptions {
  /** 掛載時是否立刻跑一次（預設 true）。 */
  immediate?: boolean
}

export function usePolling(
  fn: () => Promise<unknown>,
  intervalMs: number,
  opts: PollingOptions = {},
): PollingHandle {
  const { immediate = true } = opts
  let timer: number | null = null
  let inFlight = false
  let queued = false
  let stopped = false

  function clear() {
    if (timer !== null) {
      window.clearTimeout(timer)
      timer = null
    }
  }

  async function run(): Promise<void> {
    if (stopped) return
    if (inFlight) {
      // 改了過濾條件時打的 refresh 不能被「同時只跑一個」吞掉（否則要等下一輪才反映）
      // → 排一次補跑；旗標是布林，最多補一次、不會堆疊。
      queued = true
      return
    }
    inFlight = true
    try {
      await fn()
    } catch {
      // 這一輪失敗就算了：下一輪照跑（頁面自己會顯示既有資料）。
    } finally {
      inFlight = false
    }
    if (queued && !stopped) {
      queued = false
      await run()
    }
  }

  function schedule() {
    clear()
    if (stopped || document.hidden) return
    timer = window.setTimeout(async () => {
      await run()
      schedule()
    }, intervalMs)
  }

  function onVisibility() {
    if (stopped) return
    if (document.hidden) clear()
    else void run().then(schedule) // 切回前景：先補一次最新狀態，再接回排程
  }

  document.addEventListener('visibilitychange', onVisibility)
  onUnmounted(() => {
    stopped = true
    queued = false
    clear()
    document.removeEventListener('visibilitychange', onVisibility)
  })

  if (immediate) void run().then(schedule)
  else schedule()

  return {
    refresh: run,
    stop() {
      stopped = true
      queued = false
      clear()
    },
    start() {
      stopped = false
      schedule()
    },
  }
}
